import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS, ABIS, ILK, ILK_WPLS } from '../utils/contracts';

// ---------------------------------------------------------------------------
// Wallet-connect notice scan: every place funds can sit that a user might not
// know about, gathered for the bell icon. All read-only; renders nothing into
// the page flow. Conditions covered:
//   · internal JDAI (vat.dai) never withdrawn to the wallet
//   · internal PLS-A collateral (vat.gem) deposited/unlocked but not withdrawn
//   · internal WPLS-A collateral on the account (e.g. auction winnings)
//   · leftover collateral on the user's CDP urns — the post-liquidation case
//   · WPLS tokens in the wallet (unwrappable to native PLS)
// ---------------------------------------------------------------------------

const DAI_MIN = 10n ** 43n;  // 0.01 JDAI [rad]
const GEM_MIN = 10n ** 18n;  // 1 PLS/WPLS [wad]

export const useNotices = (account) => {
  const [data, setData] = useState({ isLoading: true, finds: null });

  const scan = useCallback(async () => {
    if (!account) { setData({ isLoading: false, finds: null }); return; }
    try {
      const provider = new ethers.JsonRpcProvider('https://rpc.pulsechain.com', 369,
        { staticNetwork: ethers.Network.from(369) });
      const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, provider);
      const wpls = new ethers.Contract(CONTRACTS.PLS, ABIS.PLS, provider);
      const registry = new ethers.Contract(CONTRACTS.PROXY_REGISTRY, ABIS.PROXY_REGISTRY, provider);

      const [daiRad, gemPls, gemWpls, walletWpls, proxy, canDaiJoin] = await Promise.all([
        vat.dai(account),
        vat.gem(ILK, account),
        vat.gem(ILK_WPLS, account),
        wpls.balanceOf(account),
        registry.proxies(account),
        vat.can(account, CONTRACTS.DAIJOIN)
      ]);

      // Leftover collateral parked on the urns of the user's CDPs — this is
      // what a liquidated vault leaves behind. It never expires and nobody
      // else can take it; the owner just has to know it's there.
      const cdpLeftovers = [];
      if (proxy && proxy !== ethers.ZeroAddress) {
        const getCdps = new ethers.Contract(CONTRACTS.GET_CDPS, ABIS.GET_CDPS, provider);
        const [ids, urns, ilks] = await getCdps.getCdpsAsc(CONTRACTS.CDP_MANAGER, proxy);
        for (let i = 0; i < ids.length; i++) {
          if (ilks[i] !== ILK_WPLS) continue;
          const [gem, [ink, art]] = await Promise.all([
            vat.gem(ILK_WPLS, urns[i]),
            vat.urns(ILK_WPLS, urns[i])
          ]);
          if (gem >= GEM_MIN) {
            cdpLeftovers.push({
              cdp: ids[i].toString(),
              urn: urns[i],
              gem,
              liquidated: ink === 0n && art === 0n
            });
          }
        }
      }

      setData({
        isLoading: false,
        finds: {
          proxy: proxy !== ethers.ZeroAddress ? proxy : null,
          daiRad: daiRad >= DAI_MIN ? daiRad : 0n,
          daiSigs: canDaiJoin === 1n ? 1 : 2, // exact count, known before the click
          gemPls: gemPls >= GEM_MIN ? gemPls : 0n,
          gemWpls: gemWpls >= GEM_MIN ? gemWpls : 0n,
          walletWpls: walletWpls >= GEM_MIN ? walletWpls : 0n,
          cdpLeftovers
        }
      });
    } catch (e) {
      // Detection is best-effort; a failed read must never break the page.
      console.warn('notice scan failed:', e);
      setData({ isLoading: false, finds: null });
    }
  }, [account]);

  useEffect(() => { scan(); }, [scan]);

  return { ...data, refresh: scan };
};
